// UI strings for installing the app (the "Asenna sovellus" page and the quiet install banner),
// Finnish (default) and English side by side. Finnish is written in full and never truncated;
// en must have exactly the same shape. iOS menu names are quoted as iOS shows them in each language.
import { defineArea } from '../area';

export const installFi = {
  title: 'Asenna sovellus',
  installedHere: 'Käytät jo asennettua sovellusta.',
  installedTab: 'Sovellus on asennettu tälle laitteelle. Avaa se aloitusnäytöltä, tehtäväpalkista tai sovellusvalikosta.',
  installButton: 'Asenna',
  installing: 'Asennetaan…',
  accepted: 'Asennettu. Sovellus löytyy nyt laitteen sovelluksista.',
  dismissedPrompt: 'Asennus peruttiin. Voit asentaa myöhemmin tästä tai selaimen valikosta.',

  iosH: 'iPhone ja iPad (Safari)',
  iosSteps: [
    'Avaa tämä sivu Safarissa.',
    'Napauta Jaa-painiketta (neliö, josta nousee nuoli). iOS 26:ssa se löytyy osoiterivin vierestä •••-valikosta.',
    'Valitse ”Lisää Koti-valikkoon”. Jos näet valinnan avata sivu verkkoappina, pidä se päällä.',
    'Napauta ”Lisää”. Avaa Valyrio sen jälkeen aina kotinäytön kuvakkeesta.',
  ],
  iosOtherNote: 'Myös Chromessa ja Edgessä (iOS 16.4 tai uudempi) Jaa-valikossa on ”Lisää Koti-valikkoon”.',
  iosNotify:
    'Päivittäinen muistutus ja kuvakkeen numero toimivat iPhonessa vain, kun sovellus on lisätty Koti-valikkoon ja avattu sieltä (iOS 16.4 tai uudempi). Salli ilmoitukset sen jälkeen sovelluksen Asetukset-näkymässä.',
  iosStorage:
    'Koti-valikon sovellus pitää omat tietonsa erillään Safarista. Kortit ja asetukset tulevat palvelimelta, kun kirjaudut sisään ensimmäisen kerran.',

  androidH: 'Android (Chrome tai Edge)',
  androidSteps: [
    'Napauta yllä olevaa Asenna-painiketta, jos se näkyy.',
    'Muuten: avaa selaimen valikko (⋮) ja valitse ”Asenna sovellus” tai ”Lisää aloitusnäytölle”.',
  ],

  desktopH: 'Tietokone (Chrome tai Edge)',
  desktopSteps: [
    'Napsauta yllä olevaa Asenna-painiketta, jos se näkyy.',
    'Muuten: napsauta osoiterivin oikeassa reunassa olevaa asennuskuvaketta, tai avaa valikko (⋮ tai …) ja valitse ”Asenna Valyrio udrir” (Edgessä Sovellukset → Asenna tämä sivusto sovelluksena).',
    'Sovellus aukeaa omaan ikkunaansa, ja sen voi kiinnittää tehtäväpalkkiin tai Dockiin.',
  ],

  otherH: 'Muut selaimet',
  otherText: 'Tämä selain ei osaa asentaa verkkosovellusta. Käytä tietokoneella Chromea tai Edgeä, iPhonessa Safaria.',

  otherPlatformsH: 'Muilla laitteilla',

  bannerText: 'Asenna Valyrio sovellukseksi.',
  bannerIos: 'Lisää Valyrio Koti-valikkoon, niin se toimii kuin sovellus ja muistutukset tulevat perille.',
  bannerHow: 'Näin asennat',
  bannerDismiss: 'Älä näytä uudelleen',
  moreRow: 'Asenna sovellus',
};

export const installEn: typeof installFi = {
  title: 'Install the app',
  installedHere: 'You are using the installed app.',
  installedTab: 'The app is installed on this device. Open it from the Home Screen, the taskbar or your apps.',
  installButton: 'Install',
  installing: 'Installing…',
  accepted: 'Installed. You will find the app among your device’s apps.',
  dismissedPrompt: 'Install cancelled. You can install later from here or from the browser menu.',

  iosH: 'iPhone and iPad (Safari)',
  iosSteps: [
    'Open this page in Safari.',
    'Tap the Share button (a square with an arrow pointing up). On iOS 26 it is in the ••• menu next to the address bar.',
    'Choose “Add to Home Screen”. If you see the option to open it as a web app, leave it on.',
    'Tap “Add”. From then on, always open Valyrio from its Home Screen icon.',
  ],
  iosOtherNote: 'Chrome and Edge (iOS 16.4 or later) also have “Add to Home Screen” in their Share menu.',
  iosNotify:
    'On iPhone the daily reminder and the icon badge only work once the app is on the Home Screen and opened from there (iOS 16.4 or later). Then allow notifications on the app’s Settings screen.',
  iosStorage: 'The Home Screen app keeps its own data, separate from Safari. Your cards and settings come from the server when you first sign in.',

  androidH: 'Android (Chrome or Edge)',
  androidSteps: ['Tap the Install button above, if it is shown.', 'Otherwise open the browser menu (⋮) and choose “Install app” or “Add to Home screen”.'],

  desktopH: 'Computer (Chrome or Edge)',
  desktopSteps: [
    'Click the Install button above, if it is shown.',
    'Otherwise click the install icon at the right end of the address bar, or open the menu (⋮ or …) and choose “Install Valyrio udrir” (in Edge: Apps → Install this site as an app).',
    'The app opens in its own window; you can pin it to the taskbar or the Dock.',
  ],

  otherH: 'Other browsers',
  otherText: 'This browser cannot install web apps. Use Chrome or Edge on a computer, Safari on iPhone.',

  otherPlatformsH: 'On other devices',

  bannerText: 'Install Valyrio as an app.',
  bannerIos: 'Add Valyrio to your Home Screen so it works like an app and reminders reach you.',
  bannerHow: 'How to install',
  bannerDismiss: 'Don’t show again',
  moreRow: 'Install the app',
};

// Messages.install (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('install', installFi, installEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof installFi> {}
}
